import { takeLatest, call, put } from 'redux-saga/effects';
import {
  SETTING_ACCOUNT_GET_ALL_DATA,
  SETTING_ACCOUNT_UPDATE_PROFILE,
  SETTING_ACCOUNT_UPDATE_BUSINESS,
  SETTING_ACCOUNT_UPDATE_PASSWORD,
  SETTING_ACCOUNT_REQUEST_OTP,
  SETTING_ACCOUNT_REQUEST_UPDATE_OTP,
  SETTING_ACCOUNT_VERIFY_OTP,
  SETTING_ACCOUNT_VERIFY_UPDATE_OTP,
  SETTING_ACCOUNT_SET_BUSINESS_LOCATION,
  SETTING_ACCOUNT_UPLOAD_NPWP_FILE,
  SETTING_ACCOUNT_NPWP_FORM_DATA,
} from 'actions/setting/account/constants';
import {
  actSetAccountProfile,
  actSetAccountBusiness,
  actSetAccountKyb,
  actSetAccountKybIncomplete,
  actSetOtpRequestId,
  actShowOtpModal,
  actSetOtpErrorMessage,
  actSetStatusCode,
  actShowDetailPage,
} from 'actions/setting/account';
import {
  success as msgSuccess,
  error as msgError,
} from 'actions/notifications';
import { getProfile } from 'sagas/api/profile';
import { updateBusiness } from 'api/business';
import { getBusinessData, getKybData, updateIdentityInfo } from 'api/kyb';
import { checkWalletRegistration } from 'api/paymentMethods';
import { logout, generateOtp as generateOtpForNewPhone } from 'api/auth';
import { generateOtp } from 'api/forgotPassword';
import {
  apiSettingAccount,
  apiVerifyOtp,
  apiVerifyUpdateOtp,
  uploadNpwpFile,
} from 'api/account';
import {
  getDropdownData,
  sendingRequest,
  showProgressBar,
  hideProgressBar,
} from 'actions';
import {
  settingAccountStart,
  settingAccountPersonalDetailsEditStart,
  settingAccountPersonalDetailsPhoneVerifyStart,
  settingAccountPersonalDetailsPhoneVerifySuccess,
  settingAccountBusinessInfoEditStart,
  settingAccountPasswordEdit,
  settingAccountPasswordEditSuccessful,
  settingAccountPersonalDetailsEditSuccess,
  settingAccountBusinessInfoEditSuccessful,
  npwpCreated,
} from 'clevertap/settings/accounts';
import { npwpImageData } from 'actions/kyb';

export function* getAllDataSaga({ payload }) {
  yield put(sendingRequest(true));
  yield put(showProgressBar());
  const { businessId } = payload;
  settingAccountStart();
  try {
    const responseProfile = yield call(getProfile);
    const responseBusiness = yield call(getBusinessData, businessId);
    const responseKyb = yield call(getKybData, businessId);
    yield put(actSetAccountProfile(responseProfile));
    yield put(actSetAccountBusiness(responseBusiness.data));
    yield put(actSetAccountKyb(responseKyb.data.data_requirement_code));
    if (responseKyb.data.data_requirement_code.NPWP_IMAGE_URL) {
      yield put(
        npwpImageData(responseKyb.data.data_requirement_code.NPWP_IMAGE_URL)
      );
    }
    let kybIncomplete;
    if (responseBusiness.data.kybRegistrationCompleted === null) {
      const outletIds = responseProfile.outlets.map(outlet => outlet.id);
      const responseCheckWallet = yield call(checkWalletRegistration, {
        outletIds,
      });
      kybIncomplete =
        responseCheckWallet.data.status === 'ALL_OUTLET_NOT_REGISTERED';
    } else {
      kybIncomplete = !responseBusiness.data.kybRegistrationCompleted;
    }
    yield put(actSetAccountKybIncomplete(kybIncomplete));
  } catch (error) {
    yield put(msgError(error.message));
  } finally {
    yield put(hideProgressBar());
    yield put(sendingRequest(false));
  }
}

export function* updateProfileSaga({ payload }) {
  yield put(sendingRequest(true));
  yield put(showProgressBar());
  const { values } = payload.data;
  if (values.user.full_name !== undefined) {
    settingAccountPersonalDetailsEditStart({
      name: values.user.full_name,
      phone: values.user.userPhone,
      email: values.user.email,
    });
  }
  try {
    yield call(apiSettingAccount, values);
    if (values.password_confirmation !== undefined) {
      settingAccountPasswordEdit();
      settingAccountPasswordEditSuccessful();
      yield call(logout);
    } else {
      settingAccountPersonalDetailsEditSuccess({
        name: values.user.full_name,
        phone: values.user.userPhone,
        email: values.user.email,
      });
    }
    yield put(msgSuccess('Profile was successfully updated.'));
    yield put(actShowDetailPage());
  } catch (error) {
    yield put(msgError(error.message));
  } finally {
    yield put(hideProgressBar());
    yield put(sendingRequest(false));
  }
}

export function* updateBusinessSaga({ payload }) {
  yield put(sendingRequest(true));
  yield put(showProgressBar());
  const { values } = payload.data;
  settingAccountBusinessInfoEditStart({
    businessName: values.name,
    businessAddress: values.address,
    province: values.province,
    city: values.city,
    district: values.kecamatan,
    postalCode: values.postal_code,
  });
  try {
    yield call(updateBusiness, values);
    yield put(msgSuccess('Business was successfully updated.'));
    settingAccountBusinessInfoEditSuccessful({
      businessName: values.name,
      businessAddress: values.address,
      province: values.province,
      city: values.city,
      district: values.kecamatan,
      postalCode: values.postal_code,
    });
    yield put(actShowDetailPage());
  } catch (error) {
    yield put(msgError(error.message));
  } finally {
    yield put(hideProgressBar());
    yield put(sendingRequest(false));
  }
}

export function* requestUpdateOtpSaga({ payload }) {
  yield put(sendingRequest(true));
  yield put(showProgressBar());
  const { phone, resend } = payload;
  settingAccountPersonalDetailsPhoneVerifyStart({ phone });
  try {
    if (phone.includes('+62')) {
      const otpResponse = yield call(generateOtpForNewPhone, phone);
      if (otpResponse.data.status_code === 0) {
        yield put(actSetOtpRequestId(otpResponse.data.request_id));
        if (resend) {
          yield put(actSetOtpErrorMessage('RESEND_OTP'));
        } else {
          yield put(actShowOtpModal(true));
          yield put(actSetOtpErrorMessage(null));
          settingAccountPersonalDetailsPhoneVerifySuccess({ phone });
        }
      } else if (otpResponse.data.status_code === 4) {
        yield put(msgError('Please try again in 30 secs'));
      } else {
        yield put(msgError(otpResponse.data.reason));
      }
    } else {
      yield put(
        msgError(
          'Please change your phone number with a valid mobile phone number'
        )
      );
    }
  } catch (error) {
    yield put(msgError(error.message));
  } finally {
    yield put(hideProgressBar());
    yield put(sendingRequest(false));
  }
}

export function* requestOtpSaga({ payload }) {
  yield put(sendingRequest(true));
  yield put(showProgressBar());
  const { phone, resend } = payload;
  try {
    if (phone) {
      if (phone.includes('+62')) {
        const otpResponse = yield call(generateOtp, phone);
        if (otpResponse.data.status_code === 0) {
          yield put(actSetOtpRequestId(otpResponse.data.request_id));
          if (resend) {
            yield put(actSetOtpErrorMessage('RESEND_OTP'));
          } else {
            yield put(actShowOtpModal(true));
            yield put(actSetOtpErrorMessage(null));
          }
        } else if (otpResponse.data.status_code === 4) {
          yield put(msgError('Please try again in 30 secs'));
        } else {
          yield put(msgError(otpResponse.data.reason));
        }
      } else {
        yield put(
          msgError(
            'Please change your phone number with a valid mobile phone number'
          )
        );
      }
    } else {
      yield put(msgError('Please input phone number before proceeding'));
    }
  } catch (error) {
    yield put(msgError(error.message));
  } finally {
    yield put(hideProgressBar());
    yield put(sendingRequest(false));
  }
}

export function* verifyUpdateOtpSaga({ payload }) {
  yield put(sendingRequest(true));
  yield put(showProgressBar());
  try {
    const response = yield call(apiVerifyUpdateOtp, payload.data);
    yield put(actSetStatusCode(response.data.status_code));
    if (response.data.status_code === 0) {
      yield put(actShowOtpModal(false));
    } else {
      yield put(actSetOtpErrorMessage(response.data.status));
    }
  } catch (error) {
    yield put(msgError(error.message));
  } finally {
    yield put(hideProgressBar());
    yield put(sendingRequest(false));
  }
}

export function* verifyOtpSaga({ payload }) {
  yield put(sendingRequest(true));
  yield put(showProgressBar());
  try {
    const response = yield call(apiVerifyOtp, payload.data);
    yield put(actSetStatusCode(response.data.status_code));
    if (response.data.status_code === 0) {
      yield put(actShowOtpModal(false));
    } else {
      yield put(actSetOtpErrorMessage(response.data.status));
    }
  } catch (error) {
    yield put(msgError(error.message));
  } finally {
    yield put(hideProgressBar());
    yield put(sendingRequest(false));
  }
}

export function* setBusinessLocationSaga({ payload }) {
  yield put(sendingRequest(true));
  yield put(showProgressBar());
  const { province, city, kecamatan } = payload.data;
  try {
    yield put(
      getDropdownData({
        province,
        city,
        kecamatan,
      })
    );
  } catch (error) {
    yield put(msgError(error.message));
  } finally {
    yield put(hideProgressBar());
    yield put(sendingRequest(false));
  }
}

export function* uploadNpwpFileSaga({ payload }) {
  yield put(sendingRequest(true));
  yield put(showProgressBar());
  const { file, id } = payload.data;
  const formData = new FormData();
  formData.append('file', file);
  formData.append('id', id);
  formData.append('namespace', 'business-identities');
  try {
    const { data: uploadResponse } = yield call(uploadNpwpFile, formData, id);
    yield put(npwpImageData(uploadResponse.download_image_url));
    $('#upload-photo').modal('hide');
  } catch (error) {
    yield put(msgError(error.message));
  } finally {
    yield put(hideProgressBar());
    yield put(sendingRequest(false));
  }
}

export function* submitNpwpSaga({ payload }) {
  yield put(sendingRequest(true));
  yield put(showProgressBar());
  const identity = {
    business_id: payload.businessId,
    data_requirement_code: {
      NPWP_NUMBER: payload.NPWP_NUMBER,
      NPWP_NAME: payload.NPWP_NAME,
      NPWP_IMAGE_URL: payload.NPWP_IMAGE_URL,
    },
  };
  try {
    yield call(updateIdentityInfo, identity, payload.businessId);
    npwpCreated({
      npwpName: payload.NPWP_NAME,
      npwpNumber: payload.NPWP_NUMBER,
    });
    yield put(actShowDetailPage());
  } catch (error) {
    yield put(msgError(error.message));
  } finally {
    yield put(hideProgressBar());
    yield put(sendingRequest(false));
  }
}

export default function* settingAccountSaga() {
  yield takeLatest(SETTING_ACCOUNT_GET_ALL_DATA, getAllDataSaga);
  yield takeLatest(SETTING_ACCOUNT_UPDATE_PROFILE, updateProfileSaga);
  yield takeLatest(SETTING_ACCOUNT_UPDATE_BUSINESS, updateBusinessSaga);
  yield takeLatest(SETTING_ACCOUNT_UPDATE_PASSWORD, updateProfileSaga);
  yield takeLatest(SETTING_ACCOUNT_REQUEST_UPDATE_OTP, requestUpdateOtpSaga);
  yield takeLatest(SETTING_ACCOUNT_VERIFY_UPDATE_OTP, verifyUpdateOtpSaga);
  yield takeLatest(SETTING_ACCOUNT_REQUEST_OTP, requestOtpSaga);
  yield takeLatest(SETTING_ACCOUNT_VERIFY_OTP, verifyOtpSaga);
  yield takeLatest(
    SETTING_ACCOUNT_SET_BUSINESS_LOCATION,
    setBusinessLocationSaga
  );
  yield takeLatest(SETTING_ACCOUNT_UPLOAD_NPWP_FILE, uploadNpwpFileSaga);
  yield takeLatest(SETTING_ACCOUNT_NPWP_FORM_DATA, submitNpwpSaga);
}
